import React from "react";
import { Row, Col } from "react-bootstrap";
import { useParams } from "react-router";
import { useSmartNavigateLegacy as useNavigate } from "components/SmartLink";
import { trackService, type TrackDetailResponse } from 'data/services/trackService';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PageTitle } from "domain/feature/hub/pageTitle/styles";
import { useTranslation } from 'react-i18next';
import { Loading } from "components/loading";
import {
  Wrapper,
  StartButton,
  Cover,
  Section,
  SidebarCard,
  LessonList,
  LessonItem,
  PlayBadge,
  Sticky,
  LayoutGrid,
  CoverArea,
  SidebarWrapper,
  StartArea,
  DescArea,
  LessonsArea,
} from "./trackAvailableDetailsPage";
import DOMPurify from "dompurify"


export const TrackAvailableDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { trackId } = useParams();
  const [data, setData] = React.useState<TrackDetailResponse | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isStarting, setIsStarting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    const fetchDetails = async () => {
      if (!trackId) return;
      try {
        setIsLoading(true);
        setError(null);
        const res = await trackService.getTrackDetails(String(trackId));
        setData(res);
        setTimeout(() => window.dispatchEvent(new Event('trackNameChanged')), 0);
      } catch (e: any) {
        console.error('Erro ao buscar detalhes da trilha', e);
        setError(e?.response?.data?.message || 'Erro ao carregar trilha');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetails();
  }, [trackId]);

  const startCourse = async () => {
    if (!trackId || !data) return;

    try {
      setIsStarting(true);

      const result = await trackService.startTrack(trackId);

      const firstLesson = data.lessons?.[0];
      if (firstLesson) {
        navigate(`/hub/learning/lesson/${firstLesson.id}`);
      } else {
        navigate('/hub/learning');
      }
    } catch (error: any) {
      console.error('Erro ao iniciar trilha:', error);

      if (error?.message?.includes('já está inscrito')) {
        navigate('/hub/learning');
        return;
      }

      console.error('Erro:', error?.message || 'Erro ao iniciar trilha');
    } finally {
      setIsStarting(false);
    }
  };

  if (isLoading) {
    return (
      <Wrapper>
        <Row className="align-items-center mb-3">
          <Col>
            <PageTitle>Carregando...</PageTitle>
          </Col>
        </Row>
        <Loading />
      </Wrapper>
    );
  }

  if (error) {
    return (
      <Wrapper>
        <Row className="align-items-center mb-3">
          <Col>
            <PageTitle>Erro ao Carregar Trilha</PageTitle>
          </Col>
        </Row>
        <div className="text-center py-5">
          <FontAwesomeIcon icon={["fas", "exclamation-triangle"]} className="text-warning mb-3" size="3x" />
          <h5 className="mb-3">Erro ao Carregar Trilha</h5>
          <p className="text-muted mb-4">{error}</p>

          <div className="d-flex gap-2 justify-content-center flex-wrap">
            <button
              className="btn btn-outline-secondary"
              onClick={() => navigate('/hub/available-tracks')}
            >
              <FontAwesomeIcon icon={["fas", "arrow-left"]} className="me-2" />
              Voltar às Trilhas
            </button>
          </div>
        </div>
      </Wrapper>
    );
  }

  if (!data) return null;

  return (
    <Wrapper>
      <Row className="align-items-center mb-3">
        <Col>
          <PageTitle data-track-name={data.title}>{data.title}</PageTitle>
        </Col>
      </Row>

      <LayoutGrid>
        <CoverArea>
          <div style={{
            width: '100%',
            height: '240px',
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: '#f8f9fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0'
          }}>
            {data.imageUrl ? (
              <Cover src={data.imageUrl} alt={data.title} />
            ) : (
              <div className="text-center text-muted">
                <FontAwesomeIcon icon={["far", "image"]} size="3x" className="mb-3" />
                <p className="mb-0">Imagem não disponível</p>
              </div>
            )}
          </div>
        </CoverArea>

        <SidebarWrapper>
          <StartArea>
            <StartButton
              onClick={startCourse}
              disabled={isStarting}
            >
              {isStarting ? (
                <>
                  <FontAwesomeIcon icon={["fas", "spinner"]} spin className="me-2" />
                  {t("Iniciando...")}
                </>
              ) : (
                t("Começar Curso")
              )}
            </StartButton>
          </StartArea>

          <LessonsArea>
            <SidebarCard
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div
                className="sidebar-header"
                style={{ fontFamily: 'sans-serif', fontWeight: 800, fontSize: 18, lineHeight: '140%', textAlign: 'left', color: '#464646' }}
              >
                {t("Conteúdo do curso")}
              </div>
              <LessonList>
                {data.lessons.map((lesson) => (
                  <LessonItem key={lesson.id} style={{ padding: '8px 0' }}>
                    <div className="title" style={{ fontSize: 14 }}>{lesson.title}</div>
                    <div className="subline" style={{ fontSize: 13 }}>
                      <img src="/video-library.svg" alt="video" width={16} height={16} style={{ marginRight: 6 }} />
                      Aula {lesson.sequence} · {lesson.minutes || 0} {t("minutos")}
                    </div>
                  </LessonItem>
                ))}
              </LessonList>
            </SidebarCard>
          </LessonsArea>
        </SidebarWrapper>

        <DescArea>
          <Section style={{ background: 'transparent', boxShadow: 'none', border: 'none' }}>
            <div className="body" style={{ background: 'transparent' }}>
              <h3 className="mb-3" style={{ fontSize: 20, fontWeight: 700 }}>
                {t("Descrição")}
              </h3>
              <hr />
              <div
                style={{ lineHeight: 1.7, marginBottom: 12, fontSize: 16 }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.description || "") }}
              />
            </div>
          </Section>
        </DescArea>
      </LayoutGrid>
    </Wrapper>
  );
};
